import { useState, useMemo, useCallback, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, BarChart, Bar, Cell } from 'recharts';
import { TrendingUp, TrendingDown, Minus, GitCompareArrows, Calendar, Filter, FileDown } from 'lucide-react';
import { DateRangeFilter } from '@/components/DateRangeFilter';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useToast } from '@/hooks/use-toast';

interface SavedSearch {
  id: string;
  topic: string;
  results: any;
  models_used: number;
  created_at: string;
}

interface Props {
  savedSearches: SavedSearch[];
  onClose: () => void;
  onDrillToSearch?: (search: SavedSearch) => void;
}

const CHART_COLORS = [
  'hsl(262, 83%, 58%)', // violet
  'hsl(199, 89%, 48%)', // cyan
  'hsl(142, 71%, 45%)', // emerald
  'hsl(38, 92%, 50%)',  // amber
  'hsl(346, 77%, 50%)', // rose
  'hsl(221, 83%, 53%)', // blue
  'hsl(280, 67%, 51%)', // purple
  'hsl(24, 95%, 53%)',  // orange
];

const sentimentToNum = (s: string): number => {
  const map: Record<string, number> = {
    very_bearish: -2, bearish: -1, neutral: 0, bullish: 1, very_bullish: 2,
  };
  return map[s?.toLowerCase()] ?? 0;
};

const sentimentLabel = (v: number): string => {
  if (v <= -1.5) return 'Very Bearish';
  if (v <= -0.5) return 'Bearish';
  if (v < 0.5) return 'Neutral';
  if (v < 1.5) return 'Bullish';
  return 'Very Bullish';
};

function extractConsensus(search: SavedSearch) {
  const results = (search.results as any[]) || [];
  const success = results.filter((r: any) => r.result);
  if (success.length === 0) return null;

  const sentiments = success.map((r: any) => sentimentToNum(r.result?.overallSentiment || 'neutral'));
  const avgSentiment = sentiments.reduce((a, b) => a + b, 0) / sentiments.length;
  const avgConfidence = Math.round(
    success.reduce((s, r: any) => s + (r.result?.confidenceScore || 0), 0) / success.length
  );

  // Aggregate signals
  const signalMap: Record<string, { signal: string; direction: string; strength: string; verificationStatus: string; modelCount: number }> = {};
  success.forEach((r: any) => {
    (r.result?.weakSignals || []).forEach((sig: any) => {
      const key = sig.signal?.toLowerCase().replace(/[^a-z0-9 ]/g, '').slice(0, 80) || '';
      if (!key) return;
      if (!signalMap[key]) {
        signalMap[key] = { ...sig, modelCount: 1 };
      } else {
        signalMap[key].modelCount++;
        if (sig.verificationStatus === 'verified') signalMap[key].verificationStatus = 'verified';
        const sr: Record<string, number> = { emerging: 0, developing: 1, strengthening: 2 };
        if ((sr[sig.strength] || 0) > (sr[signalMap[key].strength] || 0)) signalMap[key].strength = sig.strength;
      }
    });
  });

  const rankedSignals = Object.values(signalMap).sort((a, b) => b.modelCount - a.modelCount);
  const totalSignals = rankedSignals.length;
  const bullish = rankedSignals.filter(s => s.direction === 'bullish').length;
  const bearish = rankedSignals.filter(s => s.direction === 'bearish').length;
  const verified = rankedSignals.filter(s => s.verificationStatus === 'verified').length;
  const totalSources = success.reduce((s, r: any) => s + (r.result?.dataQuality?.sourcesReferenced || 0), 0);

  return {
    id: search.id,
    topic: search.topic,
    date: search.created_at,
    modelsUsed: search.models_used,
    avgSentiment,
    avgConfidence,
    totalSignals,
    bullish,
    bearish,
    neutral: totalSignals - bullish - bearish,
    verified,
    totalSources,
    signals: rankedSignals,
  };
}

export const WeakSentimentTrendComparison = ({ savedSearches, onClose, onDrillToSearch }: Props) => {
  const { toast } = useToast();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => {
    return new Set(savedSearches.slice(0, 5).map(s => s.id));
  });
  const [topicFilter, setTopicFilter] = useState<string>('all');
  const [fromDate, setFromDate] = useState<Date | undefined>();
  const [toDate, setToDate] = useState<Date | undefined>();
  const [sparkTooltip, setSparkTooltip] = useState<{ x: number; y: number; label: string; value: string } | null>(null);

  const uniqueTopics = useMemo(() => {
    const topics = new Set(savedSearches.map(s => s.topic));
    return Array.from(topics);
  }, [savedSearches]);

  const filteredSearches = useMemo(() => {
    return savedSearches.filter(s => {
      if (topicFilter !== 'all' && s.topic !== topicFilter) return false;
      if (fromDate && new Date(s.created_at) < fromDate) return false;
      if (toDate) {
        const end = new Date(toDate);
        end.setHours(23, 59, 59, 999);
        if (new Date(s.created_at) > end) return false;
      }
      return true;
    });
  }, [savedSearches, topicFilter, fromDate, toDate]);

  const toggleId = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(filteredSearches.map(s => s.id)));
  const deselectAll = () => setSelectedIds(new Set());

  const selectedData = useMemo(() => {
    return filteredSearches
      .filter(s => selectedIds.has(s.id))
      .map(extractConsensus)
      .filter(Boolean)
      .sort((a, b) => new Date(a!.date).getTime() - new Date(b!.date).getTime()) as NonNullable<ReturnType<typeof extractConsensus>>[];
  }, [filteredSearches, selectedIds]);

  // Sentiment trend chart data
  const sentimentTrendData = useMemo(() => {
    return selectedData.map(d => ({
      date: new Date(d.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      fullDate: new Date(d.date).toLocaleString(),
      topic: d.topic.length > 30 ? d.topic.slice(0, 30) + '…' : d.topic,
      sentiment: Math.round(d.avgSentiment * 100) / 100,
      confidence: d.avgConfidence,
      signals: d.totalSignals,
      bullish: d.bullish,
      bearish: d.bearish,
      verified: d.verified,
      sources: d.totalSources,
    }));
  }, [selectedData]);

  // Signal evolution: track recurring signals across searches
  const signalEvolution = useMemo(() => {
    if (selectedData.length < 2) return [];
    const signalTracker: Record<string, { signal: string; appearances: { date: string; topic: string; direction: string; strength: string; verified: string; modelCount: number }[] }> = {};
    selectedData.forEach(d => {
      d.signals.forEach(sig => {
        const key = sig.signal?.toLowerCase().replace(/[^a-z0-9 ]/g, '').slice(0, 80) || '';
        if (!key) return;
        if (!signalTracker[key]) signalTracker[key] = { signal: sig.signal, appearances: [] };
        signalTracker[key].appearances.push({
          date: new Date(d.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
          topic: d.topic,
          direction: sig.direction,
          strength: sig.strength,
          verified: sig.verificationStatus,
          modelCount: sig.modelCount,
        });
      });
    });
    return Object.values(signalTracker)
      .filter(s => s.appearances.length >= 2)
      .sort((a, b) => b.appearances.length - a.appearances.length)
      .slice(0, 12);
  }, [selectedData]);

  const selectedCount = filteredSearches.filter(s => selectedIds.has(s.id)).length;

  const exportTrendPdf = useCallback(() => {
    if (selectedData.length < 2) {
      toast({ title: 'Not enough data', description: 'Select at least 2 searches to export.', variant: 'destructive' });
      return;
    }

    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pw = doc.internal.pageSize.getWidth();
    const margin = 14;
    let y = 16;

    // Title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(124, 58, 237);
    doc.text('Weak Sentiment Trend Analysis', pw / 2, y, { align: 'center' });
    y += 8;
    doc.setFontSize(9);
    doc.setTextColor(140, 140, 140);
    doc.text(`Generated ${new Date().toLocaleString()} · ${selectedData.length} searches compared · Triple-Loop Verified`, pw / 2, y, { align: 'center' });
    y += 10;

    // Summary banner
    const latestD = selectedData[selectedData.length - 1];
    const firstD = selectedData[0];
    const sentimentDelta = latestD.avgSentiment - firstD.avgSentiment;
    const confDelta = latestD.avgConfidence - firstD.avgConfidence;

    doc.setFillColor(124, 58, 237);
    doc.roundedRect(margin, y, pw - 2 * margin, 14, 3, 3, 'F');
    doc.setFontSize(8);
    doc.setTextColor(255, 255, 255);
    const summaryLabels = [
      `Searches: ${selectedData.length}`,
      `Period: ${new Date(firstD.date).toLocaleDateString()} - ${new Date(latestD.date).toLocaleDateString()}`,
      `Sentiment Trend: ${sentimentDelta > 0 ? '+' : ''}${sentimentDelta.toFixed(2)}`,
      `Confidence Trend: ${confDelta > 0 ? '+' : ''}${confDelta}%`,
    ];
    const colW = (pw - 2 * margin) / summaryLabels.length;
    summaryLabels.forEach((lbl, i) => {
      doc.text(lbl, margin + colW * i + colW / 2, y + 9, { align: 'center' });
    });
    y += 20;

    // Sentiment Evolution Table
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(40, 40, 40);
    doc.text('Sentiment Evolution Over Time', margin, y);
    y += 4;

    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      head: [['Date', 'Topic', 'Sentiment', 'Confidence', 'Signals', 'Bullish', 'Bearish', 'Verified', 'Sources']],
      body: selectedData.map(d => [
        new Date(d.date).toLocaleDateString(),
        d.topic.length > 35 ? d.topic.slice(0, 35) + '...' : d.topic,
        sentimentLabel(d.avgSentiment),
        `${d.avgConfidence}%`,
        `${d.totalSignals}`,
        `${d.bullish}`,
        `${d.bearish}`,
        `${d.verified}`,
        `${d.totalSources}`,
      ]),
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [124, 58, 237], textColor: 255, fontStyle: 'bold' },
      columnStyles: { 1: { cellWidth: 40 } },
    });
    y = (doc as any).lastAutoTable.finalY + 8;

    // Recurring Signals
    if (signalEvolution.length > 0) {
      if (y > 230) { doc.addPage(); y = 16; }
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(40, 40, 40);
      doc.text(`Recurring Signals Across Searches (${signalEvolution.length})`, margin, y);
      y += 4;

      const strengthRank: Record<string, number> = { emerging: 0, developing: 1, strengthening: 2 };

      autoTable(doc, {
        startY: y,
        margin: { left: margin, right: margin },
        head: [['Signal', 'Appearances', 'Direction', 'Trend', 'First Seen', 'Last Seen']],
        body: signalEvolution.map(sig => {
          const first = sig.appearances[0];
          const last = sig.appearances[sig.appearances.length - 1];
          const evolved = (strengthRank[last.strength] || 0) > (strengthRank[first.strength] || 0);
          const weakened = (strengthRank[last.strength] || 0) < (strengthRank[first.strength] || 0);
          return [
            sig.signal.length > 60 ? sig.signal.slice(0, 60) + '...' : sig.signal,
            `${sig.appearances.length}x`,
            last.direction,
            evolved ? 'Strengthening' : weakened ? 'Weakening' : 'Stable',
            first.date,
            last.date,
          ];
        }),
        styles: { fontSize: 7, cellPadding: 2 },
        headStyles: { fillColor: [124, 58, 237], textColor: 255, fontStyle: 'bold' },
        columnStyles: { 0: { cellWidth: 55 } },
      });
      y = (doc as any).lastAutoTable.finalY + 8;
    }

    // Signal volume summary
    if (y > 240) { doc.addPage(); y = 16; }
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(40, 40, 40);
    doc.text('Signal Volume & Direction Summary', margin, y);
    y += 4;

    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      head: [['Date', 'Topic', 'Bullish', 'Neutral', 'Bearish', 'Verified', 'Total']],
      body: selectedData.map(d => [
        new Date(d.date).toLocaleDateString(),
        d.topic.length > 35 ? d.topic.slice(0, 35) + '...' : d.topic,
        `${d.bullish}`,
        `${d.neutral}`,
        `${d.bearish}`,
        `${d.verified}`,
        `${d.totalSignals}`,
      ]),
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [124, 58, 237], textColor: 255, fontStyle: 'bold' },
      columnStyles: { 1: { cellWidth: 40 } },
    });

    // Footer on every page
    const pageCount = doc.getNumberOfPages();
    for (let p = 1; p <= pageCount; p++) {
      doc.setPage(p);
      doc.setFontSize(7);
      doc.setTextColor(160, 160, 160);
      doc.text(
        'Triple-Loop Verified · Anti-Hallucination Guardrails · For research purposes only — not financial advice.',
        pw / 2, doc.internal.pageSize.getHeight() - 6, { align: 'center' }
      );
      doc.text(`Page ${p}/${pageCount}`, pw - margin, doc.internal.pageSize.getHeight() - 6, { align: 'right' });
    }

    const filename = `weak-sentiment-trends-${new Date().toISOString().slice(0, 10)}.pdf`;
    doc.save(filename);
    toast({ title: 'PDF exported', description: `Downloaded ${filename}` });
  }, [selectedData, signalEvolution, toast]);

  return (
    <Card className="border-violet-500/20">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-full bg-violet-500/15 flex items-center justify-center">
              <GitCompareArrows className="h-4 w-4 text-violet-600 dark:text-violet-400" />
            </div>
            <div>
              <CardTitle className="text-base">Sentiment Trend Comparison</CardTitle>
              <CardDescription>Track how signals evolve across {selectedCount} selected searches</CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={exportTrendPdf}
              disabled={selectedData.length < 2}
            >
              <FileDown className="h-3.5 w-3.5" />
              PDF Export
            </Button>
            <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Topic Filter</label>
            <Select value={topicFilter} onValueChange={setTopicFilter}>
              <SelectTrigger className="w-[220px] h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Topics</SelectItem>
                {uniqueTopics.map(t => (
                  <SelectItem key={t} value={t} className="text-xs">{t.length > 40 ? t.slice(0, 40) + '…' : t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DateRangeFilter fromDate={fromDate} toDate={toDate} onFromChange={setFromDate} onToChange={setToDate} />
          <div className="flex gap-1.5">
            <Button variant="ghost" size="sm" onClick={selectAll} className="text-xs h-8">Select All</Button>
            <Button variant="ghost" size="sm" onClick={deselectAll} className="text-xs h-8">Deselect</Button>
          </div>
        </div>

        {/* Search selector */}
        <div className="max-h-48 overflow-y-auto rounded-lg border divide-y">
          {filteredSearches.map((search, idx) => (
            <label
              key={search.id}
              className="flex items-center gap-3 px-3 py-2 hover:bg-muted/30 cursor-pointer transition-colors"
            >
              <Checkbox
                checked={selectedIds.has(search.id)}
                onCheckedChange={() => toggleId(search.id)}
              />
              <div
                className="h-3 w-3 rounded-full shrink-0"
                style={{ backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }}
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate text-foreground">{search.topic}</p>
                <p className="text-[10px] text-muted-foreground">
                  {new Date(search.created_at).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  {' · '}{search.models_used} models
                </p>
              </div>
            </label>
          ))}
          {filteredSearches.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-6">No searches match filters</p>
          )}
        </div>

        {selectedData.length >= 2 ? (
          <>
            {/* Delta Summary Card */}
            {(() => {
              const first = selectedData[0];
              const latest = selectedData[selectedData.length - 1];
              const sentDelta = latest.avgSentiment - first.avgSentiment;
              const confDelta = latest.avgConfidence - first.avgConfidence;
              const signalDelta = latest.totalSignals - first.totalSignals;
              const bullDelta = latest.bullish - first.bullish;
              const bearDelta = latest.bearish - first.bearish;
              const verifiedDelta = latest.verified - first.verified;

              const formatDelta = (v: number, suffix = '') => {
                const sign = v > 0 ? '+' : '';
                return `${sign}${Number.isInteger(v) ? v : v.toFixed(2)}${suffix}`;
              };
              const deltaColor = (v: number, invert = false) => {
                const positive = invert ? v < 0 : v > 0;
                const negative = invert ? v > 0 : v < 0;
                if (positive) return 'text-emerald-600 dark:text-emerald-400';
                if (negative) return 'text-rose-600 dark:text-rose-400';
                return 'text-muted-foreground';
              };
              const deltaIcon = (v: number) => {
                if (v > 0) return <TrendingUp className="h-3.5 w-3.5" />;
                if (v < 0) return <TrendingDown className="h-3.5 w-3.5" />;
                return <Minus className="h-3.5 w-3.5" />;
              };

              return (
                <div className="rounded-lg border bg-gradient-to-r from-violet-500/5 to-transparent p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground">Shift Summary</p>
                      <p className="text-[10px] text-muted-foreground">
                        {new Date(first.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} → {new Date(latest.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Badge variant="outline" className="text-[10px]">{sentimentLabel(first.avgSentiment)}</Badge>
                      <span className="text-muted-foreground text-xs">→</span>
                      <Badge variant="outline" className="text-[10px]">{sentimentLabel(latest.avgSentiment)}</Badge>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                    {[
                      { label: 'Sentiment', value: formatDelta(sentDelta), delta: sentDelta, invert: false, series: selectedData.map(d => d.avgSentiment) },
                      { label: 'Confidence', value: formatDelta(confDelta, '%'), delta: confDelta, invert: false, series: selectedData.map(d => d.avgConfidence) },
                      { label: 'Signals', value: formatDelta(signalDelta), delta: signalDelta, invert: false, series: selectedData.map(d => d.totalSignals) },
                      { label: 'Bullish', value: formatDelta(bullDelta), delta: bullDelta, invert: false, series: selectedData.map(d => d.bullish) },
                      { label: 'Bearish', value: formatDelta(bearDelta), delta: bearDelta, invert: true, series: selectedData.map(d => d.bearish) },
                      { label: 'Verified', value: formatDelta(verifiedDelta), delta: verifiedDelta, invert: false, series: selectedData.map(d => d.verified) },
                    ].map((item, i) => {
                      const series = item.series;
                       const min = Math.min(...series);
                       const max = Math.max(...series);
                       const range = max - min || 1;
                       const w = 80;
                       const h = 28;
                       const pad = 3;
                       const coords = series.map((v, idx) => ({
                         x: series.length === 1 ? w / 2 : pad + (idx / (series.length - 1)) * (w - pad * 2),
                         y: pad + (h - pad * 2) - ((v - min) / range) * (h - pad * 2),
                         value: v,
                         dateLabel: selectedData[idx] ? new Date(selectedData[idx].date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : `#${idx + 1}`,
                         searchId: selectedData[idx]?.id,
                       }));
                       const points = coords.map(c => `${c.x},${c.y}`).join(' ');
                       const sparkColor = item.delta > 0
                         ? (item.invert ? '#e11d48' : '#059669')
                         : item.delta < 0
                           ? (item.invert ? '#059669' : '#e11d48')
                           : '#888';

                       return (
                         <div key={i} className="rounded-md border bg-card p-2 text-center">
                           <p className="text-[10px] text-muted-foreground">{item.label}</p>
                           <div className={`flex items-center justify-center gap-1 mt-0.5 font-bold text-sm ${deltaColor(item.delta, item.invert)}`}>
                             {deltaIcon(item.delta)}
                             <span>{item.value}</span>
                           </div>
                            <div className="relative mx-auto mt-1" style={{ width: w, height: h }}>
                              <svg width={w} height={h} className={onDrillToSearch ? "cursor-pointer" : "cursor-crosshair"} viewBox={`0 0 ${w} ${h}`}
                                onMouseLeave={() => setSparkTooltip(null)}
                              >
                                <polyline
                                  points={points}
                                  fill="none"
                                  stroke={sparkColor}
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                {coords.map((c, ci) => (
                                  <g key={ci}
                                    onMouseEnter={(e) => {
                                      const rect = (e.currentTarget.closest('.relative') as HTMLElement)?.getBoundingClientRect();
                                      const svgRect = (e.currentTarget.closest('svg') as SVGElement)?.getBoundingClientRect();
                                      if (!rect || !svgRect) return;
                                      const fmtVal = typeof c.value === 'number' ? (Number.isInteger(c.value) ? String(c.value) : c.value.toFixed(2)) : String(c.value);
                                      setSparkTooltip({
                                        x: svgRect.left - rect.left + c.x,
                                        y: svgRect.top - rect.top + c.y,
                                        label: c.dateLabel,
                                        value: fmtVal,
                                      });
                                    }}
                                    onClick={() => {
                                      if (onDrillToSearch && c.searchId) {
                                        const match = savedSearches.find(s => s.id === c.searchId);
                                        if (match) onDrillToSearch(match);
                                      }
                                    }}
                                    className={onDrillToSearch ? "cursor-pointer" : ""}
                                  >
                                    <circle cx={c.x} cy={c.y} r="8" fill="transparent" stroke="none" />
                                    <circle
                                      cx={c.x} cy={c.y} r={sparkTooltip?.label === c.dateLabel ? 4 : 2}
                                      fill={sparkColor}
                                      opacity={sparkTooltip?.label === c.dateLabel ? 1 : 0}
                                      className="transition-all duration-150"
                                    />
                                    {sparkTooltip?.label === c.dateLabel && onDrillToSearch && (
                                      <circle
                                        cx={c.x} cy={c.y} r="6"
                                        fill="none"
                                        stroke={sparkColor}
                                        strokeWidth="1.5"
                                        opacity={0.5}
                                        className="animate-ping"
                                      />
                                    )}
                                  </g>
                                ))}
                              </svg>
                              {sparkTooltip && (
                                <div
                                  className="absolute z-50 pointer-events-none rounded-md border bg-popover px-2 py-1 text-[10px] text-popover-foreground shadow-md whitespace-nowrap"
                                  style={{
                                    left: sparkTooltip.x,
                                    top: sparkTooltip.y - 28,
                                    transform: 'translateX(-50%)',
                                  }}
                                >
                                  <span className="font-semibold">{sparkTooltip.label}</span>: {sparkTooltip.value}
                                </div>
                              )}
                            </div>
                         </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Sentiment & Confidence Over Time */}
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Calendar className="h-4 w-4 text-violet-500" />
                Sentiment & Confidence Over Time
              </h4>
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={sentimentTrendData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} className="fill-muted-foreground" />
                    <YAxis yAxisId="sentiment" domain={[-2.5, 2.5]} tick={{ fontSize: 11 }} className="fill-muted-foreground" label={{ value: 'Sentiment', angle: -90, position: 'insideLeft', style: { fontSize: 10, fill: 'hsl(var(--muted-foreground))' } }} />
                    <YAxis yAxisId="confidence" orientation="right" domain={[0, 100]} tick={{ fontSize: 11 }} className="fill-muted-foreground" label={{ value: 'Confidence %', angle: 90, position: 'insideRight', style: { fontSize: 10, fill: 'hsl(var(--muted-foreground))' } }} />
                    <Tooltip
                      contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: 12 }}
                      labelStyle={{ color: 'hsl(var(--foreground))' }}
                      formatter={(value: any, name: string) => {
                        if (name === 'Sentiment') return [sentimentLabel(value), name];
                        return [value, name];
                      }}
                    />
                    <Legend />
                    <Line yAxisId="sentiment" type="monotone" dataKey="sentiment" stroke="hsl(262, 83%, 58%)" strokeWidth={2.5} dot={{ r: 4 }} name="Sentiment" />
                    <Line yAxisId="confidence" type="monotone" dataKey="confidence" stroke="hsl(142, 71%, 45%)" strokeWidth={2} dot={{ r: 3 }} strokeDasharray="5 5" name="Confidence %" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Signal Volume Bar Chart */}
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Filter className="h-4 w-4 text-violet-500" />
                Signal Volume & Direction
              </h4>
              <div className="h-52 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={sentimentTrendData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} className="fill-muted-foreground" />
                    <YAxis tick={{ fontSize: 11 }} className="fill-muted-foreground" />
                    <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: 12 }} />
                    <Legend />
                    <Bar dataKey="bullish" stackId="dir" fill="hsl(142, 71%, 45%)" name="Bullish" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="bearish" stackId="dir" fill="hsl(346, 77%, 50%)" name="Bearish" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="verified" fill="hsl(262, 83%, 58%)" name="Verified" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Sentiment Intensity Heatmap */}
            {(() => {
              // Build heatmap: rows = unique topics, columns = unique dates
              const topicDateMap: Record<string, Record<string, { sentiment: number; confidence: number }>> = {};
              const allDates: string[] = [];
              const allTopics: string[] = [];

              selectedData.forEach(d => {
                const dateLabel = new Date(d.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                const topicLabel = d.topic.length > 35 ? d.topic.slice(0, 35) + '…' : d.topic;

                if (!allDates.includes(dateLabel)) allDates.push(dateLabel);
                if (!allTopics.includes(topicLabel)) allTopics.push(topicLabel);

                if (!topicDateMap[topicLabel]) topicDateMap[topicLabel] = {};
                topicDateMap[topicLabel][dateLabel] = { sentiment: d.avgSentiment, confidence: d.avgConfidence };
              });

              if (allTopics.length < 1 || allDates.length < 1) return null;

              const getCellColor = (sentiment: number) => {
                if (sentiment >= 1.5) return 'bg-emerald-600 text-white';
                if (sentiment >= 0.5) return 'bg-emerald-400/80 text-emerald-950';
                if (sentiment > -0.5) return 'bg-muted-foreground/20 text-foreground';
                if (sentiment > -1.5) return 'bg-rose-400/80 text-rose-950';
                return 'bg-rose-600 text-white';
              };

              const getCellOpacity = (confidence: number) => {
                return Math.max(0.4, confidence / 100);
              };

              return (
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-violet-500" />
                    Sentiment Intensity Heatmap
                  </h4>
                  <div className="overflow-x-auto rounded-lg border">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b bg-muted/30">
                          <th className="text-left py-2 px-3 font-medium text-muted-foreground sticky left-0 bg-muted/30 z-10 min-w-[160px]">Topic</th>
                          {allDates.map(date => (
                            <th key={date} className="text-center py-2 px-2 font-medium text-muted-foreground whitespace-nowrap">{date}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {allTopics.map((topic, ti) => (
                          <tr key={ti} className="border-b last:border-0">
                            <td className="py-1.5 px-3 font-medium text-foreground sticky left-0 bg-card z-10 truncate max-w-[200px]" title={topic}>
                              {topic}
                            </td>
                            {allDates.map(date => {
                              const cell = topicDateMap[topic]?.[date];
                              if (!cell) {
                                return <td key={date} className="py-1.5 px-2 text-center"><span className="text-muted-foreground/30">—</span></td>;
                              }
                              return (
                                <td key={date} className="py-1.5 px-1 text-center">
                                  <div
                                    className={`inline-flex flex-col items-center justify-center rounded-md px-2 py-1 min-w-[56px] ${getCellColor(cell.sentiment)}`}
                                    style={{ opacity: getCellOpacity(cell.confidence) }}
                                    title={`${sentimentLabel(cell.sentiment)} · ${cell.confidence}% confidence`}
                                  >
                                    <span className="font-bold text-[11px]">{cell.sentiment > 0 ? '+' : ''}{cell.sentiment.toFixed(1)}</span>
                                    <span className="text-[8px] opacity-80">{cell.confidence}%</span>
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* Heatmap legend */}
                  <div className="flex items-center justify-center gap-1.5 mt-2">
                    <span className="text-[9px] text-muted-foreground">V.Bearish</span>
                    <div className="flex h-3 rounded-sm overflow-hidden">
                      <div className="w-5 bg-rose-600" />
                      <div className="w-5 bg-rose-400/80" />
                      <div className="w-5 bg-muted-foreground/20" />
                      <div className="w-5 bg-emerald-400/80" />
                      <div className="w-5 bg-emerald-600" />
                    </div>
                    <span className="text-[9px] text-muted-foreground">V.Bullish</span>
                    <span className="text-[9px] text-muted-foreground ml-3">Opacity = confidence</span>
                  </div>
                </div>
              );
            })()}

            {signalEvolution.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-violet-500" />
                  Recurring Signals Across Searches ({signalEvolution.length})
                </h4>
                <div className="space-y-2.5">
                  {signalEvolution.map((sig, i) => {
                    const latestApp = sig.appearances[sig.appearances.length - 1];
                    const firstApp = sig.appearances[0];
                    const strengthRank: Record<string, number> = { emerging: 0, developing: 1, strengthening: 2 };
                    const evolved = (strengthRank[latestApp.strength] || 0) > (strengthRank[firstApp.strength] || 0);
                    const weakened = (strengthRank[latestApp.strength] || 0) < (strengthRank[firstApp.strength] || 0);

                    return (
                      <div key={i} className="rounded-lg border p-3 hover:bg-muted/30 transition-colors">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-foreground">{sig.signal}</p>
                            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                              <Badge className="bg-violet-500/15 text-violet-700 dark:text-violet-400 border-violet-500/30 text-xs">
                                {sig.appearances.length}× across searches
                              </Badge>
                              <Badge variant={latestApp.direction === 'bullish' ? 'default' : latestApp.direction === 'bearish' ? 'destructive' : 'secondary'} className="text-xs">
                                {latestApp.direction}
                              </Badge>
                              {evolved && (
                                <Badge className="bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30 text-xs gap-1">
                                  <TrendingUp className="h-3 w-3" /> Strengthening
                                </Badge>
                              )}
                              {weakened && (
                                <Badge className="bg-muted text-muted-foreground text-xs gap-1">
                                  <TrendingDown className="h-3 w-3" /> Weakening
                                </Badge>
                              )}
                              {!evolved && !weakened && (
                                <Badge variant="outline" className="text-xs gap-1">
                                  <Minus className="h-3 w-3" /> Stable
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-[10px] text-muted-foreground">First: {firstApp.date}</p>
                            <p className="text-[10px] text-muted-foreground">Latest: {latestApp.date}</p>
                          </div>
                        </div>
                        {/* Timeline dots */}
                        <div className="flex items-center gap-1 mt-2">
                          {sig.appearances.map((app, j) => (
                            <div key={j} className="flex items-center gap-1" title={`${app.date} · ${app.topic} · ${app.strength}`}>
                              <div className={`h-2.5 w-2.5 rounded-full ${
                                app.strength === 'strengthening' ? 'bg-amber-500' : app.strength === 'developing' ? 'bg-blue-500' : 'bg-muted-foreground/40'
                              }`} />
                              {j < sig.appearances.length - 1 && <div className="w-4 h-px bg-border" />}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {signalEvolution.length === 0 && selectedData.length >= 2 && (
              <div className="text-center py-6 text-sm text-muted-foreground">
                No recurring signals found across the selected searches. Try selecting searches on the same topic.
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-10 text-muted-foreground">
            <GitCompareArrows className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium">Select at least 2 searches to compare</p>
            <p className="text-xs mt-1">Use the checkboxes above to pick searches for trend analysis</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
